import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import styles from './mega-nav-mobile.module.css';
import { NavigationMenu, PageReference } from '../../../typescript/response';
import { Chevron } from '../../icon/chevron';
import Button from '../../ui/button';
import LanguageSelector from '../../ui/language-selector';
import { CSLocale } from '../../../typescript/component';
import { getCachedAsset } from '../../../helper/utils';

interface MegaNavMobileProps {
  navMenu: NavigationMenu[];
  isOpenMobileSideBar?: boolean;
  handleCloseSideBar?: (arg: boolean) => void;
  isMenuLevelTwoOpened: boolean;
  handleMenuLevelTwoOpened: (arg: boolean) => void;
  showLanguageSelector?: boolean;
  languages: CSLocale[] | null;
  currentLocale: string;
  onLocaleChange: (newLocale: string) => void;
  locations?: PageReference;
  request_quote: PageReference;
  contact_us: PageReference;
}

export default function MegaNavMobile({
  navMenu,
  isOpenMobileSideBar = false,
  handleCloseSideBar,
  handleMenuLevelTwoOpened,
  isMenuLevelTwoOpened,
  showLanguageSelector,
  languages,
  currentLocale,
  onLocaleChange,
  locations,
  request_quote,
  contact_us,
}: MegaNavMobileProps) {
  const [menuLevelTwo, setMenuLevelTwo] = useState<NavigationMenu | null>(null);
  const [mobileCTAHeight, setMobileCTAHeight] = useState<number | null>(null);
  const mobileCTARef = useRef<HTMLDivElement | null>(null);
  const navMobileContainerL2Ref = useRef<HTMLDivElement | null>(null);

  const selectMenu = (menu?: NavigationMenu) => {
    handleMenuLevelTwoOpened(!isMenuLevelTwoOpened);
    if (menu) setMenuLevelTwo(menu);
  };

  const selectLink = () => {
    handleCloseSideBar && handleCloseSideBar(false);
  };

  const getHash = (hash?: string) => (!hash ? '' : hash.startsWith('#') ? hash : `#${hash}`);

  useEffect(() => {
    if (mobileCTARef.current) {
      setMobileCTAHeight(mobileCTARef.current.offsetHeight);
    }
    const handleResize = () => {
      if (mobileCTARef.current) {
        setMobileCTAHeight(mobileCTARef.current.offsetHeight);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isOpenMobileSideBar) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
      handleMenuLevelTwoOpened(false);
    }
  }, [isOpenMobileSideBar]);

  return (
    <div className={`${styles.navMobileContainer} ${isOpenMobileSideBar ? styles.open : ''}`}>
      <nav
        style={{
          height: mobileCTAHeight ? `calc(100% - ${mobileCTAHeight}px - 15px)` : '100%',
          overflowY: 'scroll',
        }}
      >
        {navMenu.map((list, idx) => {
          const hasSub = list.sub_navigation?.every(p => p?.page_reference?.length > 0);
          return (
            <div key={idx} className={styles.navbarLink}>
              {hasSub ? (
                <div className={styles.menuItemWrapper} onClick={() => selectMenu(list)}>
                  <span className={styles.menuMobileLink}>{list.label}</span>
                  <Chevron direction="right" variant="none" />
                </div>
              ) : (
                <Link
                  href={list.page_reference[0].url}
                  locale={currentLocale}
                  className={`${styles.menuMobileLink} ${styles.menuItemWrapper}`}
                  onClick={selectLink}
                >
                  {list.label}
                </Link>
              )}
            </div>
          );
        })}
      </nav>

      {menuLevelTwo && (
        <nav
          ref={navMobileContainerL2Ref}
          className={`${styles.navMobileContainerL2} ${isOpenMobileSideBar && isMenuLevelTwoOpened ? styles.open : ''}`}
          style={{
            height: mobileCTAHeight ? `calc(100% - ${mobileCTAHeight}px - 15px)` : '100%',
            overflowY: 'scroll',
          }}
        >
          <div className={`${styles.navbarLink} ${styles.navbarLinkTitleL2}`}>
            <div className={styles.menuItemL2Wrapper}>
              <Link
                href={menuLevelTwo.page_reference?.[0]?.url}
                locale={currentLocale}
                className={styles.multiSlide}
                onClick={selectLink}
              >
                {menuLevelTwo.label}
              </Link>
              <Chevron direction="right" variant="none" />
            </div>
          </div>
          {menuLevelTwo?.sub_navigation?.map(sub => (
            <div key={sub.label} className={`${styles.navbarLink} ${styles.navbarLinkL2}`}>
              <div className={styles.menuItemWrapper}>
                <Link
                  href={`${sub.page_reference[0].url}${
                    sub.querystring ? `?${sub.querystring}` : ''
                  }${getHash(sub.hash)}`}
                  locale={currentLocale}
                  className={`${styles.multiSlide} ${styles.navbarLinkL2Item}`}
                  onClick={selectLink}
                >
                  {sub.label}
                  {sub.title_image?.url && (
                    <img className={styles.logoImageDropdown} src={getCachedAsset(sub.title_image.url)} alt="" />
                  )}
                </Link>
              </div>
            </div>
          ))}
        </nav>
      )}

      <div ref={mobileCTARef} className={styles.mobileCTA}>
        <div className={styles.mobileCTAButtons}>
          {request_quote?.page_reference?.[0]?.url && (
            <Button
              text={request_quote.label}
              showIcon
              propWidth="11.25rem"
              propBackgroundColor="var(--white)"
              propHoverBackgroundColor="var(--brand-primary-500)"
              propHoverText="var(--white)"
              propTextColor="var(--primary-500)"
              link={`/${currentLocale}${request_quote.page_reference[0].url}`}
              onClick={selectLink}
            />
          )}
          {contact_us?.page_reference?.[0]?.url && (
            <Button
              text={contact_us.label}
              showIcon
              propWidth="11.25rem"
              propBackgroundColor="var(--primary-500)"
              propHoverBackgroundColor="transparent"
              propTextColor="var(--white)"
              link={`/${currentLocale}${contact_us.page_reference[0].url}`}
              onClick={selectLink}
            />
          )}
        </div>
        <div className={styles.mobileBottomWrapper}>
          {locations?.page_reference?.[0]?.url && (
            <span className={styles.mobileLocationsLink}>
              <Link href={locations.page_reference[0].url} locale={currentLocale}>
                {locations.label}
              </Link>
            </span>
          )}
          {showLanguageSelector && (
            <LanguageSelector
              languages={languages}
              currentLocale={currentLocale}
              onLocaleChange={onLocaleChange}
            />
          )}
        </div>
      </div>
    </div>
  );
}
